import { CSSProperties } from 'react';
import { TextElementView } from '../../types/ElementView';
import { getFontAttributesFromTextElementView } from '../../util/elementViewHelpers';

export const getPlaceCardAlignment = (
  verticalAlignmentValue: 'top' | 'middle' | 'bottom',
  horizontalAlignmentValue: 'left' | 'right' | 'center'
): CSSProperties => {
  const baseStyle: CSSProperties = { display: 'flex' };
  let verticalAlignmentStyle: CSSProperties = {};
  let horizontalAlignmentStyle: CSSProperties = {};

  switch (verticalAlignmentValue) {
    case 'top':
      verticalAlignmentStyle = { alignItems: 'start' };
      break;

    case 'bottom':
      verticalAlignmentStyle = { alignItems: 'end' };
      break;

    case 'middle':
      verticalAlignmentStyle = { alignItems: 'center' };
      break;

    default:
      break;
  }

  switch (horizontalAlignmentValue) {
    case 'left':
      horizontalAlignmentStyle = { justifyContent: 'start' };
      break;

    case 'right':
      horizontalAlignmentStyle = { justifyContent: 'end' };
      break;

    case 'center':
      horizontalAlignmentStyle = { justifyContent: 'center' };
      break;

    default:
      break;
  }

  return { ...baseStyle, ...verticalAlignmentStyle, ...horizontalAlignmentStyle };
};

const isVerticalAlignment = (alignment?: string): alignment is 'top' | 'middle' | 'bottom' => {
  if (!alignment) return false;

  return ['top', 'middle', 'bottom'].includes(alignment);
};

const isHorizontalAlignment = (alignment?: string): alignment is 'left' | 'right' | 'center' => {
  if (!alignment) return false;

  return ['left', 'right', 'center'].includes(alignment);
};

/**
 * Converts the backend alignment value (TOP|BOTTOM|MIDDLE) to the CSS value (top|middle|bottom) with a default of top
 *
 * @see https://github.com/NewAmsterdamLabs/svc-paper/blob/3928a06499d9f0af39a1c3ecb69c31bcc3858f87/entities/src/main/java/com/zola/service/paper/entities/card/model/common/VerticalAlignment.java
 * @param alignment
 * @returns
 */
const getVerticalAlignment = (alignment: string | undefined): 'top' | 'middle' | 'bottom' => {
  const alignmentLowerCase = alignment?.toLowerCase() || 'top';

  if (isVerticalAlignment(alignmentLowerCase)) {
    return alignmentLowerCase;
  }
  return 'top';
};

/**
 *
 * Converts the backend alignment value (LEFT|RIGHT|CENTER) to the CSS value (left|center|right) with a default of left
 *
 * @see https://github.com/NewAmsterdamLabs/svc-paper/blob/3928a06499d9f0af39a1c3ecb69c31bcc3858f87/server/src/main/java/com/zola/service/paper/core/card/parser/core/enums/HorizontalAlignment.java
 * @param alignment
 */
const getHorizontalAlignment = (alignment: string | undefined): 'left' | 'right' | 'center' => {
  const alignmentLowerCase = alignment?.toLowerCase() || 'left';

  if (isHorizontalAlignment(alignmentLowerCase)) {
    return alignmentLowerCase;
  }

  return 'left';
};

export const getElementAlignmentCss = (
  element: TextElementView
): {
  verticalAlignment: 'top' | 'middle' | 'bottom';
  horizontalAlignment: 'left' | 'right' | 'center';
} => {
  const { horizontalAlignment, verticalAlignment } = getFontAttributesFromTextElementView(element);

  return {
    verticalAlignment: getVerticalAlignment(verticalAlignment),
    horizontalAlignment: getHorizontalAlignment(horizontalAlignment),
  };
};
